import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Gem, PenLine, Shapes, Boxes, ArrowRight, Sparkles } from "lucide-react";
import { useMetals, useConfig, fmtMoney } from "../hooks/useMetals";
import { useLang } from "../i18n";

const FALLBACK = {
  fees: {
    casting_base: 45, casting_per_gram: 6, finishing_per_gram: 2,
    gem_setting_per_stone: 18, engraving_per_char: 2.5,
    wax_carving_flat: 120, cad_model_flat: 95, prototype_3d_flat: 60,
    complexity: { simple: 1.0, detailed: 1.25, intricate: 1.5 },
  },
  purity: {
    XAU: { "24K": 0.999, "18K": 0.75, "14K": 0.585 },
    XAG: { "Sterling 925": 0.925 },
    XPT: { "Platinum 950": 0.95 },
    XPD: { "Palladium 950": 0.95 },
  },
  metals: { XAU: "Gold", XAG: "Silver", XPT: "Platinum", XPD: "Palladium" },
};

const METAL_ACCENTS = { XAU: "#C9A84C", XAG: "#9AA3AD", XPT: "#C3CBD2", XPD: "#8C7B5B" };
const METAL_NAMES = {
  XAU: { en: "Gold", it: "Oro" },
  XAG: { en: "Silver", it: "Argento" },
  XPT: { en: "Platinum", it: "Platino" },
  XPD: { en: "Palladium", it: "Palladio" },
};
const COMPLEXITY_LABELS = {
  simple: { en: "Simple", it: "Semplice" },
  detailed: { en: "Detailed", it: "Dettagliata" },
  intricate: { en: "Intricate", it: "Articolata" },
};

export default function Calculator() {
  const metals = useMetals();
  const cfg = useConfig() || FALLBACK;
  const { lang, L } = useLang();
  const navigate = useNavigate();
  const [metal, setMetal] = useState("XAU");
  const [purity, setPurity] = useState("18K");
  const [weight, setWeight] = useState(8);
  const [complexity, setComplexity] = useState("detailed");
  const [stones, setStones] = useState(0);
  const [engrave, setEngrave] = useState(0);
  const [wax, setWax] = useState(false);
  const [cad, setCad] = useState(false);
  const [proto, setProto] = useState(false);
  const [currency, setCurrency] = useState("EUR");
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  const parseDescription = async () => {
    if (!aiText.trim()) return;
    setAiBusy(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/estimate-parse`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: aiText, lang }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "parse failed");
      setMetal(d.metal);
      setPurity(d.purity);
      setWeight(Math.round(d.weight_grams));
      setComplexity(d.complexity);
      setStones(d.stones);
      setEngrave(d.engraving_chars);
      setWax(d.wax_carving);
      setCad(d.cad_model);
      setProto(d.prototype_3d);
      toast.success(L({ en: "Workbench filled from your description — adjust anything.", it: "Banco compilato dalla tua descrizione — modifica pure." }));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAiBusy(false);
    }
  };

  const quote = useMemo(() => {
    const spot = metals?.prices?.find((p) => p.symbol === metal);
    const fx = currency === "EUR" ? 1 : metals ? 1 / metals.usd_eur : 1;
    const rate = spot ? (currency === "EUR" ? spot.eur_per_gram : spot.usd_per_gram) : 0;
    const pf = cfg.purity[metal][purity] ?? 1;
    const f = cfg.fees;
    const mult = f.complexity[complexity];
    const lines = [
      { label: `${purity.toLowerCase().startsWith(L(METAL_NAMES[metal]).toLowerCase().slice(0, 4)) ? purity : `${L(METAL_NAMES[metal])} ${purity}`} × ${weight} g`, amount: rate * pf * weight },
      { label: `${L({ en: "Casting", it: "Fusione" })} · ${L(COMPLEXITY_LABELS[complexity]).toLowerCase()}`, amount: (f.casting_base + f.casting_per_gram * weight * mult) * fx },
      { label: L({ en: "Finishing & polish", it: "Finitura e lucidatura" }), amount: f.finishing_per_gram * weight * fx },
    ];
    if (stones > 0) lines.push({ label: `${L({ en: "Gemstone setting", it: "Incastonatura" })} × ${stones}`, amount: stones * f.gem_setting_per_stone * fx });
    if (engrave > 0) lines.push({ label: `${L({ en: "Engraving", it: "Incisione" })} × ${engrave} ${L({ en: "chars", it: "caratteri" })}`, amount: engrave * f.engraving_per_char * fx });
    if (wax) lines.push({ label: L({ en: "Hand wax carving", it: "Scultura in cera a mano" }), amount: f.wax_carving_flat * fx });
    if (cad) lines.push({ label: L({ en: "CAD 3D modelling", it: "Modellazione CAD 3D" }), amount: f.cad_model_flat * fx });
    if (proto) lines.push({ label: L({ en: "3D wax prototype", it: "Prototipo in cera 3D" }), amount: f.prototype_3d_flat * fx });
    return { lines, total: lines.reduce((s, l) => s + l.amount, 0), rate: rate * pf, fx };
  }, [metals, cfg, metal, purity, weight, complexity, stones, engrave, wax, cad, proto, currency, lang]);

  const pickMetal = (m) => {
    setMetal(m);
    setPurity(Object.keys(cfg.purity[m])[m === "XAU" ? 1 : 0]);
  };

  const addon = (active) =>
    `flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-all duration-300 ${
      active ? "border-[#C9A84C] bg-[#C9A84C]/10 text-[#8A6D2B]" : "border-[#0E3B33]/15 text-[#4A5248] hover:border-[#C9A84C]/60 hover:text-[#0E3B33]"
    }`;

  const sendEstimate = () => {
    const summary = (lang === "it"
      ? `Richiesta preventivo — ${L(METAL_NAMES[metal])} ${purity}, ${weight} g, fusione ${L(COMPLEXITY_LABELS[complexity]).toLowerCase()}`
      : `Estimate request — ${L(METAL_NAMES[metal])} ${purity}, ${weight} g, ${complexity} casting`) +
      (stones ? (lang === "it" ? `, ${stones} pietre` : `, ${stones} stones`) : "") +
      (engrave ? (lang === "it" ? `, incisione ${engrave} caratteri` : `, engraving ${engrave} chars`) : "") +
      (wax ? (lang === "it" ? ", scultura in cera" : ", wax carving") : "") +
      (cad ? (lang === "it" ? ", modello CAD" : ", CAD model") : "") +
      (proto ? (lang === "it" ? ", prototipo 3D" : ", 3D prototype") : "") +
      (lang === "it" ? ` — totale indicativo ${fmtMoney(quote.total, currency)} alla quotazione live.` : ` — indicative total ${fmtMoney(quote.total, currency)} at live spot.`);
    navigate("/contact", { state: { prefill: summary } });
  };

  return (
    <section id="calculator" data-testid="interactive-jewellery-cost-calculator" className="relative border-y hairline bg-[#F2EDE3] py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-16 flex flex-wrap items-end justify-between gap-8"
        >
          <div className="max-w-xl">
            <p className="mono-tech mb-5 text-[#A8853B]">{L({ en: "The Workbench — Live Estimate", it: "Il Banco — Preventivo Live" })}</p>
            <h2 className="font-display text-4xl leading-tight tracking-tight text-[#0E3B33] sm:text-5xl">
              {L({ en: "What would your", it: "Quanto costerebbe il tuo" })} <span className="gold-text italic">{L({ en: "piece", it: "pezzo" })}</span> {L({ en: "cost today?", it: "oggi?" })}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[#4A5248]">
              {L({
                en: "Live market quotation of the metal, plus honest workshop labour. Move the sliders — the number breathes with the market.",
                it: "Quotazione di mercato live del metallo, più il costo onesto della manodopera. Muovi i cursori — il numero respira con il mercato.",
              })}
            </p>
          </div>
          <div className="flex items-center gap-2 rounded-full border border-[#0E3B33]/15 bg-white p-1" data-testid="currency-toggle">
            {["EUR", "USD"].map((c) => (
              <button
                key={c}
                data-testid={`currency-${c.toLowerCase()}-btn`}
                onClick={() => setCurrency(c)}
                className={`rounded-full px-5 py-1.5 text-sm font-medium transition-colors duration-300 ${currency === c ? "bg-[#0E3B33] text-[#FBF9F3]" : "text-[#4A5248] hover:text-[#0E3B33]"}`}
              >
                {c === "EUR" ? "€ EUR" : "$ USD"}
              </button>
            ))}
          </div>
        </motion.div>

        {/* AI estimate copilot */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mb-12 rounded-xl border border-[#C9A84C]/40 bg-white p-6"
          data-testid="estimate-copilot"
        >
          <div className="mono-tech mb-3 flex items-center gap-2 text-[#A8853B]">
            <Sparkles className="h-3.5 w-3.5" /> {L({ en: "Describe it — we fill the workbench", it: "Descrivilo — compilamo noi il banco" })}
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <input
              data-testid="estimate-copilot-input"
              className="forge-field"
              placeholder={L({
                en: 'e.g. "18K gold signet with a hand-engraved family crest and 3 small diamonds"',
                it: 'es. "Sigillo in oro 18K con stemma di famiglia inciso a mano e 3 piccoli diamanti"',
              })}
              value={aiText}
              onChange={(e) => setAiText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && parseDescription()}
            />
            <button
              data-testid="estimate-copilot-btn"
              onClick={parseDescription}
              disabled={aiBusy || !aiText.trim()}
              className="ghost-btn shrink-0 !py-2.5 disabled:opacity-50"
            >
              {aiBusy ? L({ en: "Reading…", it: "Lettura…" }) : L({ en: "Fill from description", it: "Compila dalla descrizione" })}
            </button>
          </div>
        </motion.div>

        <div className="grid gap-10 lg:grid-cols-12">{/* controls */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="space-y-10 lg:col-span-7"
          >
            <div>
              <div className="mono-tech mb-4 text-[#8A8F82]">01 — {L({ en: "Metal", it: "Metallo" })}</div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {Object.keys(cfg.metals).map((sym) => (
                  <button
                    key={sym}
                    data-testid={`metal-select-${METAL_NAMES[sym].en.toLowerCase()}`}
                    onClick={() => pickMetal(sym)}
                    className={`rounded-lg border bg-white p-4 text-left transition-all duration-300 ${
                      metal === sym ? "border-[#C9A84C] shadow-md" : "border-[#0E3B33]/10 hover:border-[#C9A84C]/50"
                    }`}
                  >
                    <div className="h-2 w-8 rounded-full" style={{ background: METAL_ACCENTS[sym] }} />
                    <div className="mt-3 font-display text-lg text-[#0E3B33]">{L(METAL_NAMES[sym])}</div>
                    <div className="mono-tech mt-1 text-[#8A8F82]">{sym}</div>
                  </button>
                ))}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {Object.keys(cfg.purity[metal]).map((label) => (
                  <button
                    key={label}
                    data-testid={`purity-${label.replace(/\s/g, "-").toLowerCase()}`}
                    onClick={() => setPurity(label)}
                    className={addon(purity === label)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="mono-tech mb-4 flex items-center justify-between text-[#8A8F82]">
                <span>02 — {L({ en: "Weight", it: "Peso" })}</span>
                <span className="text-[#A8853B]" data-testid="weight-readout">{weight} g</span>
              </div>
              <input
                type="range"
                min="1"
                max="150"
                value={weight}
                data-testid="weight-slider"
                onChange={(e) => setWeight(Number(e.target.value))}
                className="forge-range"
                style={{ "--fill": `${(weight / 150) * 100}%` }}
              />
              <div className="mono-tech mt-2 flex justify-between text-[#8A8F82]"><span>1 g</span><span>150 g</span></div>
            </div>

            <div>
              <div className="mono-tech mb-4 text-[#8A8F82]">03 — {L({ en: "Casting complexity", it: "Complessità di fusione" })}</div>
              <div className="flex flex-wrap gap-2">
                {Object.keys(cfg.fees.complexity).map((c) => (
                  <button key={c} data-testid={`complexity-${c}`} onClick={() => setComplexity(c)} className={addon(complexity === c)}>
                    {L(COMPLEXITY_LABELS[c])}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="mono-tech mb-4 text-[#8A8F82]">04 — {L({ en: "Craft services", it: "Lavorazioni" })}</div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="rounded-lg border border-[#0E3B33]/10 bg-white p-4">
                  <div className="flex items-center gap-2 text-sm text-[#1E2B26]"><Gem className="h-4 w-4 text-[#A8853B]" /> {L({ en: "Gemstones to set", it: "Pietre da incastonare" })}</div>
                  <div className="mt-3 flex items-center gap-3">
                    <button data-testid="stones-minus" onClick={() => setStones(Math.max(0, stones - 1))} className="h-8 w-8 rounded-full border border-[#0E3B33]/15 text-[#1E2B26] transition-colors hover:border-[#C9A84C]">−</button>
                    <span className="w-8 text-center font-display text-xl text-[#A8853B]" data-testid="stones-count">{stones}</span>
                    <button data-testid="stones-plus" onClick={() => setStones(Math.min(60, stones + 1))} className="h-8 w-8 rounded-full border border-[#0E3B33]/15 text-[#1E2B26] transition-colors hover:border-[#C9A84C]">+</button>
                  </div>
                </div>
                <div className="rounded-lg border border-[#0E3B33]/10 bg-white p-4">
                  <div className="flex items-center gap-2 text-sm text-[#1E2B26]"><PenLine className="h-4 w-4 text-[#A8853B]" /> {L({ en: "Engraving characters", it: "Caratteri di incisione" })}</div>
                  <input
                    type="number" min="0" max="200" value={engrave}
                    data-testid="engraving-chars-input"
                    onChange={(e) => setEngrave(Math.max(0, Math.min(200, Number(e.target.value) || 0)))}
                    className="forge-field mt-3 !py-2"
                  />
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button data-testid="addon-wax-carving" onClick={() => setWax(!wax)} className={addon(wax)}><Shapes className="h-4 w-4" /> {L({ en: "Hand wax carving", it: "Scultura in cera a mano" })}</button>
                <button data-testid="addon-cad-model" onClick={() => setCad(!cad)} className={addon(cad)}><Boxes className="h-4 w-4" /> {L({ en: "CAD 3D modelling", it: "Modellazione CAD 3D" })}</button>
                <button data-testid="addon-prototype" onClick={() => setProto(!proto)} className={addon(proto)}><Gem className="h-4 w-4" /> {L({ en: "3D wax prototype", it: "Prototipo in cera 3D" })}</button>
              </div>
            </div>
          </motion.div>

          {/* breakdown */}
          <motion.aside
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5"
          >
            <div className="sticky top-28 rounded-xl border border-[#C9A84C]/40 bg-white/90 p-7 shadow-xl backdrop-blur-xl" data-testid="quote-breakdown-panel">
              <div className="flex items-center justify-between">
                <span className="mono-tech text-[#8A8F82]">{L({ en: "Estimate breakdown", it: "Dettaglio preventivo" })}</span>
                <span className={`mono-tech flex items-center gap-2 ${metals?.stale ? "text-amber-600" : "text-emerald-700"}`} data-testid="quote-spot-status">
                  <span className={`h-1.5 w-1.5 rounded-full ${metals?.stale ? "bg-amber-500" : "bg-emerald-500"} animate-pulse`} />
                  {metals ? (metals.stale ? L({ en: "Delayed spot", it: "Spot differito" }) : L({ en: "Live spot", it: "Spot live" })) : L({ en: "Connecting", it: "Connessione" })}
                </span>
              </div>
              <div className="mono-tech mt-2 text-[#8A8F82]">
                Spot {L(METAL_NAMES[metal])} {purity}: {metals ? `${fmtMoney(quote.rate, currency)}/g` : "···"}
              </div>
              <div className="mt-6 space-y-3.5 border-t hairline pt-6">
                {quote.lines.map((l, i) => (
                  <motion.div key={l.label} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, delay: i * 0.04 }} className="flex items-baseline justify-between gap-4 text-sm">
                    <span className="text-[#4A5248]">{l.label}</span>
                    <span className="whitespace-nowrap text-[#1E2B26]" data-testid={`quote-line-${i}`}>{fmtMoney(l.amount, currency)}</span>
                  </motion.div>
                ))}
              </div>
              <div className="mt-6 flex items-end justify-between border-t border-[#C9A84C]/40 pt-6">
                <span className="mono-tech text-[#8A8F82]">{L({ en: "Indicative total", it: "Totale indicativo" })}</span>
                <motion.span
                  key={quote.total.toFixed(2) + currency}
                  initial={{ opacity: 0.4, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35 }}
                  className="font-display text-4xl gold-text"
                  data-testid="quote-total"
                >
                  {fmtMoney(quote.total, currency)}
                </motion.span>
              </div>
              <p className="mt-4 text-xs leading-relaxed text-[#8A8F82]">
                {L({
                  en: "Indicative only — metal is invoiced at the spot rate on casting day. Final quote confirmed after design review.",
                  it: "Solo indicativo — il metallo viene fatturato alla quotazione del giorno di fusione. Preventivo finale confermato dopo la revisione del design.",
                })}
              </p>
              <button
                data-testid="send-quote-request-btn"
                onClick={sendEstimate}
                className="ember-btn mt-6 w-full justify-center"
              >
                {L({ en: "Send this estimate", it: "Invia questo preventivo" })} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}
